import { type DataSourceType } from '@libraries/typescript/api_client';
import { assert } from '@libraries/typescript/utilities';

const HHAEXCHANGE_ENTERPRISE_PATH_SEGMENT_PATTERN = /^ENT\d+$/i;

export const DATA_SOURCE_OPTIONS: Array<{
    value: DataSourceType;
    label: string;
}> = [
    { value: 'wellsky', label: 'WellSky' },
    { value: 'axiscare', label: 'AxisCare' },
    { value: 'hhaexchange', label: 'HHAExchange' },
    { value: 'alayacare', label: 'AlayaCare' },
];

export function getDataSourceDisplayName(type: DataSourceType): string {
    switch (type) {
        case 'wellsky':
            return 'WellSky';
        case 'axiscare':
            return 'AxisCare';
        case 'generations_homecare':
            return 'Generations Homecare';
        case 'hhaexchange':
            return 'HHAExchange';
        case 'alayacare':
            return 'AlayaCare';
        default:
            assert.never(type);
    }
}

export function resolveCaregiverEhrLink(
    caregiver:
        | {
              readonly external_id?: string | null;
              readonly external_url_id?: string | null;
              readonly source_system?: DataSourceType | null;
          }
        | null
        | undefined,
    dataSourceAccounts:
        | ReadonlyArray<{
              readonly type: DataSourceType;
              readonly base_url: string;
          }>
        | null
        | undefined,
): { readonly href: string; readonly label: string } | null {
    const caregiverExternalId =
        caregiver?.external_url_id ?? caregiver?.external_id;
    if (!caregiverExternalId || !caregiver?.source_system) {
        return null;
    }
    const dataSourceAccount = dataSourceAccounts?.find((account) => {
        return account.type === caregiver.source_system;
    });
    if (!dataSourceAccount) {
        return null;
    }
    return {
        href: generateExternalCaregiverUrl(
            caregiver.source_system,
            caregiverExternalId,
            dataSourceAccount.base_url,
        ),
        label: `View in ${getDataSourceDisplayName(caregiver.source_system)}`,
    };
}

export function generateExternalCaregiverUrl(
    sourceSystem: DataSourceType,
    externalId: string,
    baseUrl: string,
): string {
    const root = normalizeBaseUrl(baseUrl);
    switch (sourceSystem) {
        case 'wellsky':
            return `${root}/caregivers/${externalId}/`;
        case 'axiscare':
            return `${root}/?caregivers-profile.php&id=${externalId}`;
        case 'generations_homecare':
            // Routing to the general caregivers list page because their specific caregiver page does not refer to caregivers by id.
            // Clicking on them assigns an id for that session.
            return `${root}/views/caregiverlist.aspx`;
        case 'hhaexchange':
            return buildHhaExchangeEntityUrl(
                baseUrl,
                `/Aide/Aide_ns.aspx?AideId=${externalId}`,
            );
        case 'alayacare':
            return `${root}/#/employees/${externalId}/schedule`;
        default:
            assert.never(sourceSystem);
    }
}

export function generateExternalClientUrl(
    sourceSystem: DataSourceType,
    externalId: string,
    baseUrl: string,
    externalVisitId?: string,
): string {
    const root = normalizeBaseUrl(baseUrl);
    switch (sourceSystem) {
        case 'wellsky':
            return `${root}/clients/${externalId}/`;
        case 'axiscare':
            return `${root}/?customers-profile.php&id=${externalId}`;
        case 'generations_homecare':
            // Routing to the general clients list page because their specific client page does not refer to clients by id.
            return `${root}/views/clientlist.aspx`;
        case 'hhaexchange':
            return buildHhaExchangeEntityUrl(
                baseUrl,
                `/Patient/InternalPatientInfo_ns.aspx?PatientId=${externalId}&FromSearch=1`,
            );
        case 'alayacare':
            return externalVisitId && /^\d+$/.test(externalVisitId)
                ? `${root}/scheduling/shift/notes/id/${externalVisitId}`
                : `${root}/#/clients/${externalId}/scheduling`;
        default:
            assert.never(sourceSystem);
    }
}

function normalizeBaseUrl(baseUrl: string): string {
    return baseUrl.replace(/\/$/, '');
}

function buildHhaExchangeEntityUrl(
    baseUrl: string,
    tenantScopedPath: string,
): string {
    const enterpriseRoot = getHhaExchangeEnterpriseRoot(baseUrl);
    if (!enterpriseRoot) {
        // HHAExchange entity pages live under a tenant-specific /ENT... prefix.
        // When that prefix is not configured, fall back to the configured entry
        // URL instead of manufacturing a broken fixture-only detail link.
        return normalizeBaseUrl(baseUrl);
    }
    return `${enterpriseRoot}${tenantScopedPath}`;
}

function getHhaExchangeEnterpriseRoot(baseUrl: string): string | null {
    try {
        const url = new URL(baseUrl);
        const enterprisePathSegment = url.pathname
            .split('/')
            .find((segment) =>
                HHAEXCHANGE_ENTERPRISE_PATH_SEGMENT_PATTERN.test(segment),
            );
        return enterprisePathSegment
            ? `${url.origin}/${enterprisePathSegment}`
            : null;
    } catch {
        return null;
    }
}
